import { Box, Container, Grid2, IconButton, Stack } from "@mui/material";
import { Package } from "../../../../domain/model/Package";
import { Player } from "../../../../domain/model/User";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { CustomTextView } from "../../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import {
  CheckCircleRounded,
  CloseRounded,
  DiscountRounded,
  SellRounded,
  SummarizeRounded,
  TagRounded,
} from "@mui/icons-material";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
} from "../../../../data/colors/Colors";
import { CustomTextField } from "../../../component/CustomTextField";
import { PaymentViewModel } from "./PaymentViewModel";
import {
  provideCheckDiscountCodeIfRedeemableUseCase,
  provideExecutePaymentUseCase,
  provideInitiatePaymentUseCase,
} from "../../../di/UseCasesModule";
import { LoadingView } from "../../../component/LoadingView";
import { EmptyView } from "../../../component/EmptyView";
import { PaymentMethod } from "../../../../domain/model/Payment";
import { CustomImageView } from "../../../component/CustomImageView";
import { CustomCardView } from "../../../component/CustomCardView";
import { CustomButtonView } from "../../../component/CustomButtonView";

export function PaymentView({
  player,
  packageItem,
  handleClose,
}: {
  player: Player;
  packageItem: Package;
  handleClose: () => void;
}) {
  const viewModel = PaymentViewModel(
    packageItem,
    player,
    provideInitiatePaymentUseCase(),
    provideExecutePaymentUseCase(),
    provideCheckDiscountCodeIfRedeemableUseCase()
  );
  const { t } = useTranslation();
  const { width, height } = useWindowDimensions();
  const isMobile = width < 700 || height < 700;
  return (
    <Stack width={isMobile ? "92vw" : "30vw"} minHeight={"100vh"} p={2} gap={2}>
      <Stack width={"100%"} alignItems={"center"}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          width={"100%"}
          justifyContent={"space-between"}
        >
          <Box width={40} />
          <Stack alignItems={"center"}>
            <SellRounded sx={{ color: COLOR_MAIN }} fontSize="large" />
            <CustomTextView
              text={t("summary")}
              fontWeight="bold"
              variant="h5"
              textColor={COLOR_MAIN}
            />
          </Stack>
          <IconButton
            onClick={handleClose}
            sx={{
              width: 40,
              height: 40,
              background: COLOR_MAIN,
            }}
          >
            <CloseRounded sx={{ color: "white" }} />
          </IconButton>
        </Stack>
        <Stack
          width={"100%"}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <CustomTextView text={t("games_number")} variant="body1" />
          <CustomTextView
            text={packageItem.numberOfGames?.toString()}
            variant="h6"
            fontWeight="bold"
          />
        </Stack>

        <Stack
          width={"100%"}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <CustomTextView text={t("price_in_dollar")} variant="body1" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            {viewModel.discount > 0 && (
              <CheckCircleRounded sx={{ color: COLOR_GREEN }} />
            )}
            {viewModel.discount > 0 && (
              <CustomTextView
                text={`$${viewModel.calculateDiscountedPriceInUSD()}`}
                variant="h6"
                fontWeight="bold"
              />
            )}
            <CustomTextView
              text={`$${packageItem.priceInUSD}`}
              variant={viewModel.discount > 0 ? "body1" : "h6"}
              fontWeight={viewModel.discount > 0 ? "normal" : "bold"}
              textColor={
                viewModel.discount > 0 ? COLOR_LIGHT_GRAY : COLOR_BLACK
              }
              props={{
                textDecoration:
                  viewModel.discount > 0 ? "line-through" : undefined,
              }}
            />
          </Stack>
        </Stack>

        <Stack
          width={"100%"}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <CustomTextView text={t("discount")} variant="body1" />
          <CustomTextView
            text={`${viewModel.discount}%`}
            variant="h6"
            fontWeight={viewModel.discount > 0 ? "bold" : "normal"}
            textColor={viewModel.discount > 0 ? COLOR_MAIN : COLOR_BLACK}
          />
        </Stack>
      </Stack>

      {viewModel.discount == 0 && (
        <Box
          component={"form"}
          onSubmit={(e) => {
            e.preventDefault();
            viewModel.checkIfDiscountCodeRedeemable();
          }}
          height={"100%"}
        >
          <Stack width={"100%"} direction={"row"} gap={2} alignItems={"center"}>
            <Stack width={"100%"}>
              <CustomTextField
                placeholder={t("discount_code")}
                onTextChange={(t) => {
                  viewModel.setDiscountCode(t);
                }}
                required
                value={viewModel.discountCode}
                startIcon={<DiscountRounded sx={{ color: COLOR_BLACK }} />}
              />
            </Stack>
            <IconButton sx={{ border: "solid 1px black", p: 2 }} type="submit">
              <DiscountRounded sx={{ color: COLOR_BLACK }} />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Stack maxWidth={"100%"} height={"100%"} justifyContent={"center"}>
        {viewModel.loading ? (
          <LoadingView minHeight="60vh" />
        ) : (
          <>
            {viewModel.paymentMethods && viewModel.paymentMethods.length > 0 ? (
              <Stack width={"100%"} alignItems={"center"} gap={2}>
                <CustomTextView
                  text={t("payment_methods")}
                  variant="h5"
                  fontWeight="bold"
                  textAlign="center"
                />
                <Grid2 container spacing={1}>
                  {viewModel.paymentMethods.map((paymentMethod, index) => {
                    return (
                      <Grid2 size={isMobile ? 12 : 6} key={index}>
                        <PaymentMethodItemView
                          paymentMethod={paymentMethod}
                          index={index}
                          onPaymentMethodClicked={() => {
                            viewModel.executePayment(
                              paymentMethod.PaymentMethodId
                            );
                          }}
                        />
                      </Grid2>
                    );
                  })}
                </Grid2>
              </Stack>
            ) : (
              <EmptyView minHeight={"60vh"} />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}

function PaymentMethodItemView({
  paymentMethod,
  index,
  onPaymentMethodClicked,
}: {
  paymentMethod: PaymentMethod;
  index: number;
  onPaymentMethodClicked: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";
  return (
    <CustomCardView
      padding={2}
      height={150}
      children={
        <Stack
          width={"100%"}
          gap={2}
          height={"100%"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} gap={2}>
            <CustomTextView
              text={`#${index + 1}`}
              variant="body1"
              fontWeight="bold"
            />
            <Stack width={"6vmax"} height={"8vmin"}>
              <CustomImageView
                width={"100%"}
                height={"100%"}
                src={paymentMethod.ImageUrl}
                scale="fill"
              />
            </Stack>

            <CustomTextView
              text={
                isRtl
                  ? paymentMethod.PaymentMethodAr
                  : paymentMethod.PaymentMethodEn
              }
              variant="body1"
              fontWeight="bold"
            />
          </Stack>

          <CustomButtonView
            text={`${t("pay")} ${paymentMethod.TotalAmount} ${
              paymentMethod.CurrencyIso
            }`}
            onClick={onPaymentMethodClicked}
          />
        </Stack>
      }
    />
  );
}
